(() => {
    const MOBILE_MAX_WIDTH = 430;
    const KEYBOARD_THRESHOLD = 80;
    const SCROLL_THRESHOLD = 8;
    const DISMISS_FLAG_TIMEOUT_MS = 150;
    const AUTH_CAPTCHA_SWAP_TARGETS = new Set([
        'login-error-container',
        'retry-error-container',
        'captcha-container',
    ]);

    let touchStartX = null;
    let touchStartY = null;
    let touchStartedOnFocusedInput = false;

    const isMobileLayout = () => window.matchMedia(`(max-width: ${MOBILE_MAX_WIDTH}px)`).matches;

    const getFocusedAuthInput = () => {
        const activeElement = document.activeElement;

        if (!(activeElement instanceof HTMLInputElement || activeElement instanceof HTMLTextAreaElement)) {
            return null;
        }

        return activeElement;
    };

    const isTargetInsideFocusedInput = target => {
        const input = getFocusedAuthInput();

        if (!input || !(target instanceof Node)) {
            return false;
        }

        return input === target || input.contains(target);
    };

    const isKeyboardLikelyOpen = () => {
        const focusedInput = getFocusedAuthInput();

        if (!focusedInput) {
            return false;
        }

        if (!window.visualViewport) {
            return true;
        }

        const layoutHeight = document.documentElement.clientHeight || window.innerHeight;
        const visualBottom = window.visualViewport.height + window.visualViewport.offsetTop;
        const keyboardOffset = layoutHeight - visualBottom;

        return keyboardOffset > KEYBOARD_THRESHOLD || document.activeElement === focusedInput;
    };

    const isDismissIgnoredTarget = target => {
        if (!(target instanceof Element)) {
            return false;
        }

        return Boolean(
            target.closest('#submit-button')
            || target.closest('#submit-captcha')
            || target.closest('#clear-input')
            || target.closest('#country-code')
            || target.closest('#country-dropdown')
            || target.closest('#auth-phone-back-button')
            || target.closest('#auth-otp-back-button')
            || target.closest('#captcha-back-button')
            || target.closest('#refresh-captcha')
            || target.closest('#hhcaptcha-refresh-container')
            || target.closest('button[type="submit"]')
            || target.closest('a[href]')
        );
    };

    const dismissKeyboard = () => {
        const input = getFocusedAuthInput();

        if (!input) {
            return;
        }

        document.documentElement.dataset.dismissKeyboard = 'true';
        input.blur();

        window.setTimeout(() => {
            delete document.documentElement.dataset.dismissKeyboard;
        }, DISMISS_FLAG_TIMEOUT_MS);
    };

    const isAuthCaptchaResponse = response => {
        return typeof response === 'string' && response.includes('id="captcha-container"');
    };

    const showAuthCaptchaView = () => {
        document.getElementById('auth-phone-view')?.classList.add('hidden');
        document.getElementById('auth-otp-view')?.classList.add('hidden');

        const captchaMount = document.getElementById('auth-captcha-mount');
        if (captchaMount) {
            captchaMount.classList.remove('hidden');
        }
    };

    const hideAuthCaptchaView = () => {
        const captchaMount = document.getElementById('auth-captcha-mount');
        if (captchaMount) {
            captchaMount.innerHTML = '';
            captchaMount.classList.add('hidden');
        }

        document.getElementById('auth-phone-view')?.classList.remove('hidden');
        document.getElementById('auth-otp-view')?.classList.remove('hidden');
    };

    const focusCaptchaInput = () => {
        const input = document.getElementById('challenge-text');
        if (!input) {
            return;
        }

        window.setTimeout(() => {
            input.focus({ preventScroll: true });
        }, 50);
    };

    const activateAuthCaptchaIfPresent = swapTarget => {
        const mount = document.getElementById('auth-captcha-mount');
        if (!mount) {
            return false;
        }

        let captcha = null;

        if (swapTarget === mount) {
            captcha = mount.querySelector('#captcha-container');
        } else if (swapTarget instanceof Element) {
            captcha = swapTarget.querySelector('#captcha-container');
            if (captcha) {
                mount.appendChild(captcha);
                swapTarget.innerHTML = '';
            }
        }

        if (!captcha) {
            return false;
        }

        showAuthCaptchaView();
        focusCaptchaInput();
        return true;
    };

    const setupAuthCaptchaSwapHandling = () => {
        const mount = document.getElementById('auth-captcha-mount');
        if (!mount) {
            return;
        }

        document.addEventListener('htmx:beforeSwap', event => {
            const response = event.detail.serverResponse;
            if (!isAuthCaptchaResponse(response)) {
                return;
            }

            const targetId = event.detail.target?.id;
            if (!targetId || !AUTH_CAPTCHA_SWAP_TARGETS.has(targetId)) {
                return;
            }

            event.detail.target = mount;
        });

        document.addEventListener('htmx:afterSwap', event => {
            if (event.target === mount || AUTH_CAPTCHA_SWAP_TARGETS.has(event.target.id)) {
                activateAuthCaptchaIfPresent(event.target);
            }
        });
    };

    const handleTouchStart = event => {
        touchStartX = null;
        touchStartY = null;
        touchStartedOnFocusedInput = false;

        if (!isMobileLayout() || !isKeyboardLikelyOpen()) {
            return;
        }

        const touch = event.touches[0];
        if (!touch) {
            return;
        }

        touchStartX = touch.clientX;
        touchStartY = touch.clientY;
        touchStartedOnFocusedInput = isTargetInsideFocusedInput(event.target);

        if (isDismissIgnoredTarget(event.target)) {
            return;
        }

        if (!touchStartedOnFocusedInput) {
            dismissKeyboard();
        }
    };

    const handleTouchMove = event => {
        if (
            !isMobileLayout()
            || !isKeyboardLikelyOpen()
            || touchStartX === null
            || touchStartY === null
            || !touchStartedOnFocusedInput
        ) {
            return;
        }

        const touch = event.touches[0];
        if (!touch) {
            return;
        }

        const deltaX = Math.abs(touch.clientX - touchStartX);
        const deltaY = Math.abs(touch.clientY - touchStartY);

        if (deltaX >= SCROLL_THRESHOLD || deltaY >= SCROLL_THRESHOLD) {
            dismissKeyboard();
            resetTouchTracking();
        }
    };

    const resetTouchTracking = () => {
        touchStartX = null;
        touchStartY = null;
        touchStartedOnFocusedInput = false;
    };

    const init = () => {
        setupAuthCaptchaSwapHandling();

        document.addEventListener('touchstart', handleTouchStart, { passive: true, capture: true });
        document.addEventListener('touchmove', handleTouchMove, { passive: true, capture: true });
        document.addEventListener('touchend', resetTouchTracking, { passive: true, capture: true });
        document.addEventListener('touchcancel', resetTouchTracking, { passive: true, capture: true });
    };

    window.authCaptchaView = {
        hide: hideAuthCaptchaView,
        show: showAuthCaptchaView,
    };

    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', init);
    } else {
        init();
    }
})();
